import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useMainStore } from '@/stores/main'

/**
 * How a track is named across the site: which of its two names leads and which one follows it, small.
 *
 * The website decides it (config/ui.php, delivered by the bootstrap — see store.trackTitlePrimary):
 * 'country' has the translated country as the title with the track's own name under it, 'track' keeps
 * the track name in front.
 */
export function useTrackTitle()
{
  const store = useMainStore()
  const { t, te } = useI18n()

  const primaryIsCountry = computed(() => store.trackTitlePrimary === 'country')

  const countryOf = trackId => store.tracks.find(track => track.id === trackId)?.country ?? null

  /** ISO code of the track's country, null when the track has none. */
  const countryCode = trackId => countryOf(trackId)

  /** Translated name of the track's country, empty when the track has none or it has no name here. */
  const countryName = trackId => {
    const country = countryOf(trackId)
    return country && te(`countries.${country}`) ? t(`countries.${country}`) : ''
  }

  /** true when the country hosts more than one track: its name alone would not name one. */
  const countryHasSeveralTracks = trackId => {
    const country = countryOf(trackId)

    return Boolean(country) && store.tracks.filter(track => track.country === country).length > 1
  }

  /** The track's own name, from the translations. */
  const trackName = trackId => (trackId ? t(`tracks.${trackId}`) : '')

  /** The name that leads: the country when the site is set up that way, the track otherwise. */
  const primaryName = trackId => (primaryIsCountry.value ? countryName(trackId) : trackName(trackId))

  /** The name that follows the leading one: the other of the two, empty when there is none. */
  const secondaryName = trackId => (primaryIsCountry.value ? trackName(trackId) : countryName(trackId))

  /** Both names in one string, where the two must stay on one line: a select entry, a badge. */
  const fullName = trackId => {
    const leading = primaryName(trackId)
    const following = secondaryName(trackId)

    return following ? `${leading} (${following})` : leading
  }

  /** The name a sentence uses: the country alone when it names one track, both names otherwise. */
  const sentenceName = trackId => {
    if (!primaryIsCountry.value || countryHasSeveralTracks(trackId)) {
      return fullName(trackId)
    }

    return countryName(trackId) || trackName(trackId)
  }

  return {
    primaryIsCountry,
    primaryName,
    secondaryName,
    fullName,
    sentenceName,
    countryCode,
    countryName,
    trackName,
  }
}
